import React, { useContext, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import PlusIcon from 'icons/Plus.icon';
import DealContext from '../../../../providers/Deal/Deal.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ColleaguesManageTable from '../ColleaguesManageTable';
import { AddUsersModal } from 'components/AddUsersModal';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';

import './ColleaguesInDealCard.styles.scss';

type ColleaguesInDealCardProps = {
  className?: string;
  isDisabled?: boolean;
};

const ColleaguesInDealCard: React.FC<ColleaguesInDealCardProps> = (props) => {
  const { className, isDisabled } = props;

  const [addColleagueModal, addColleagueModalRef] =
    useCallbackRef<ModalRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    dealRoom,
    colleagues,
    areColleaguesLoading,
    isOwner,
    setColleagues,
    openTransferDrawer,
  } = useContext(DealContext);

  const { _id: dealRoomId } = dealRoom || {};

  const classes = classNames('colleagues-in-deal-card', className);

  const canAddColleague = useMemo(
    () => isOwner || isSuperAdmin,
    [isOwner, isSuperAdmin],
  );

  const { t } = useTranslation();

  const onSubmitHandle = useCallback(
    async (emails: string[]) => {
      try {
        const { data } = await api.dealRoom.createColleagues(
          dealRoomId,
          emails,
        );

        setColleagues(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, popServerError, setColleagues],
  );

  return (
    <div className={classes}>
      <div className="heading">
        <p>
          {t('yourColleagues')}
          {!areColleaguesLoading && (
            <Counter
              count={colleagues.length}
              variant={COUNTER_VARIANTS.GREY}
            />
          )}
        </p>
        {canAddColleague && (
          <Button
            variant="outline"
            icon={PlusIcon}
            iconPosition="right"
            onClick={() => addColleagueModal.open()}
          >
            {t('add')}
          </Button>
        )}
      </div>

      {areColleaguesLoading ? (
        <Loader />
      ) : (
        <>
          <ColleaguesManageTable
            isDisabled={isDisabled}
            openTransferDrawer={openTransferDrawer}
          />
          <div className="reminder">
            <p>{t('reminderThatColleaguesNeedToAccept')}</p>
          </div>
        </>
      )}

      <AddUsersModal
        ref={addColleagueModalRef}
        title={t('addColleagueToThisDeal')}
        submitText={t('inviteColleagues')}
        handleOnSubmit={onSubmitHandle}
      />
    </div>
  );
};

export default ColleaguesInDealCard;
